import { forwardRef, type HTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/cn';

/**
 * Reproduces index.css's .badge-warn/-info/-success/-danger exactly (same
 * four colours, same shape) via the semantic tokens defined in
 * src/styles/theme.css, rather than a fresh palette — see Button.tsx's
 * reasoning on visual parity during migration.
 *
 * These four are STATUS colours (Waiting/In Consultation/Completed, Paid/
 * Pending, etc.) — fixed regardless of hospital branding, unlike Button's
 * `primary` variant which follows --teal. A status badge means the same
 * clinical/financial thing everywhere; it must not shift because a hospital
 * picked red as their primary colour.
 *
 * `caution` is a fifth, added once --color-caution-ink existed (see
 * theme.css) — same background as warn, a distinct amber ink for "elevated
 * concern, not routine pending" (Schedule H1 medicines, in ScheduleBadge).
 */
const badgeVariants = cva(
  'inline-flex items-center rounded-full px-[9px] py-[2px] text-[11px] font-semibold',
  {
    variants: {
      variant: {
        neutral: 'bg-muted text-muted-foreground',
        warn: 'bg-warn-soft text-warn-ink',
        caution: 'bg-warn-soft text-caution-ink',
        info: 'bg-info-soft text-info-ink',
        success: 'bg-success-soft text-success-ink',
        danger: 'bg-danger-soft text-danger-ink',
      },
    },
    defaultVariants: { variant: 'neutral' },
  }
);

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {}

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(({ className, variant, ...props }, ref) => (
  <span ref={ref} className={cn(badgeVariants({ variant }), className)} {...props} />
));
Badge.displayName = 'Badge';
